<template>
    <div>
        <div class="vx-text-slate-700 vx-text-base vx-font-bold vx-mb-6">Request Headers</div>

        <vx-tabs v-if="!(data.auth_config_type === 'oauth2_client_credentials' && data.auth_config_id)" size="lg" v-model="currentTab" class="vx-mb-6">
            <vx-tab v-if="!data.auth_config_id || data.auth_config_type === 'custom_header'" value="basic" data-test="basic">Basic Auth</vx-tab>
            <vx-tab v-if="!data.auth_config_id || data.auth_config_type === 'custom_header'" value="custom" data-test="custom">Custom</vx-tab>
            <vx-tab v-if="!data.auth_config_id" value="oauth" data-test="oauth">OAuth</vx-tab>
        </vx-tabs>

        <div v-if="currentTab == 'basic'">
            <vx-label>Username</vx-label>
            <vx-input
                autocomplete="off"
                size="lg"
                v-model="basic.username"
                :error="displayFirstError(validate.username)"></vx-input>

            <vx-label class="vx-mt-4">Password</vx-label>
            <vx-input size="lg"
                autocomplete="off"
                :type="showSecret ? 'text' : 'password'"
                :error="displayFirstError(validate.password)"
                v-model="basic.password">
                <template v-slot:right>
                    <vx-button class="-vx-mr-1"
                         @click.prevent="showSecret = !showSecret"
                        color="transparent"
                        size="sm">
                        <font-awesome-icon :icon="showSecret ? faEyeSlash : faEye"></font-awesome-icon>
                    </vx-button>
                </template>
            </vx-input>
        </div>

        <div v-if="currentTab == 'custom'">
            <vx-label>Name</vx-label>
            <vx-input class="vx-mb-4"
                size="lg"
                v-model="data.header_name"
                :error="displayFirstError(validate.header_name)"></vx-input>

            <div data-test="header-value-wrapper">
                <vx-label>Value</vx-label>
                <vx-input
                    size="lg"
                    :type="showSecret ? 'text' : 'password'"
                    :error="displayFirstError(validate.header_value)"
                    v-model="data.header_value"
                    @focus="focusSecret"
                    :placeholder="data.auth_config_id ? '•••••••••' : ''"
                    @blur="showSecret = false">
                </vx-input>
            </div>
        </div>

        <div v-if="currentTab === 'oauth'" data-test="oauth-form">
            <div class="vx-mb-4">
                <vx-label>Token Endpoint</vx-label>
                <vx-input
                    size="lg"
                    :error="displayFirstError(validate.token_endpoint)"
                    v-model="data.token_endpoint"></vx-input>
            </div>

            <div class="vx-mb-4">
                <vx-label>Client ID</vx-label>
                <vx-input
                    size="lg"
                    :error="displayFirstError(validate.client_id)"
                    v-model="data.client_id">
                </vx-input>
            </div>

            <div>
                <vx-label>Client Secret</vx-label>
                <vx-input
                    size="lg"
                    :error="displayFirstError(validate.client_secret)"
                    v-model="data.client_secret"
                    :placeholder="data.auth_config_id ? '•••••••••' : ''"
                    :type="showSecret ? 'text' : 'password'"
                    @focus="focusSecret"
                    @blur="showSecret = false">
                </vx-input>
            </div>
        </div>
    </div>
</template>

<script setup>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/pro-solid-svg-icons";
import {
    VxTabs,
    VxTab,
    VxInput,
    VxButton,
    VxLabel
} from "@voxie/frontend-components";
import { ref, watch, reactive, nextTick } from 'vue';
import useVuelidate from "@vuelidate/core";
import { computed } from "vue";
import { helpers, maxLength, required, requiredIf, url } from "@vuelidate/validators";
import { displayFirstError } from "~/utils/validation";
import { useAuthConfigForm } from "~/components/settings/integrations/auth-configs/form/useAuthConfigForm";

const { data, payload } = useAuthConfigForm();

const currentTab = ref('basic');

const showSecret = ref(false);

const basic = reactive({
    username: '',
    password: '',
})

watch(() => data.value.auth_config_id, () => {
    if (data.value.auth_config_id) {
        currentTab.value = data.value.auth_config_type === 'oauth2_client_credentials' ? 'oauth' : 'custom';
    } else {
        currentTab.value = 'basic'
    }
}, {
    immediate: true,
});

watch(() => currentTab.value, () => {
    data.value.auth_config_type = currentTab.value === 'oauth' ? 'oauth2_client_credentials' : 'custom_header';
})

watch(basic, () => {
    data.value.header_name = 'Authorization';
    data.value.header_value = 'Basic ' + btoa(`${basic.username}:${basic.password}`);
});

const focusSecret = async () => {
    await nextTick();
    showSecret.value = true;
}

const rules = computed(() => {
    if (currentTab.value == 'basic') {
        return {
            username: {
                required,
            },
            password: {
                required,
            }
        }
    } else if (currentTab.value === 'oauth') {
        return {
            token_endpoint: {
                required,
                maxLength: maxLength(2048),
                url,
                startsWith: helpers.withMessage(
                    'Token Endpoint should start with \'https://\'.',
                    (value) => value?.startsWith('https://') || false,
                ),
            },
            client_id: {
                required,
                maxLength: maxLength(100),
                regex: helpers.withMessage(
                    'Client ID is invalid.',
                    helpers.regex(/^[!-~]+$/),
                ),
            },
            client_secret: {
                requiredIf: requiredIf(() => !data.value.auth_config_id || payload.value.client_id),
                maxLength: maxLength(255),
                regex: helpers.withMessage(
                    'Client Secret is invalid.',
                    helpers.regex(/^[!-~]+$/),
                ),
            },
        };
    }

    return {
        header_name: {
            requiredIf: requiredIf(() => !data.value.auth_config_id || payload.value.header_value),
        },
        header_value: {
            requiredIf: requiredIf(() => !data.value.auth_config_id || payload.value.header_name),
        },
    };
});

const validate = useVuelidate(rules, computed(() => ({...basic, ...data.value,})));
</script>
